import './DashboardBuilder.scss';
import { useState, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CardSize,
  CHART_TYPES,
  ChartId,
  ChartType,
  ResolvedRoleLevels
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { clearWorkingCopyFor } from '../Charts/hooks/useWorkingCopy';
import {
  useAnyChartUsesComparison,
  useSavedChartSummaries
} from '../DashboardAgent/useDashboardingGrounding';
import { DragProvider } from './drag/DragContext';
import { DragOverlay } from './drag/DragOverlay';
import { DashboardGrid } from './DashboardGrid';
import { DashboardCard } from './DashboardCard';
import {
  DashboardConfig,
  DashboardCard as DashboardCardData,
  DashboardSection,
  CardTypeValue,
  CardType,
  ChartCardConfig
} from './dashboard-types';
import {
  applyLayoutChanges,
  autoCompact,
  byGridOrder,
  compactBySection,
  INITIAL_PENDING_FILTER_FLOW,
  insertAtIndex,
  mergeExternalLayout,
  pendingDashboardFilters,
  pendingFilterFlow,
  placementSignature,
  removeCard,
  removeShownCardFromSaved,
  resizeCardInList,
  savedCardFor,
  withPendingDefaultFilters
} from './dashboard-utils';
import { DashboardFilterLine } from './DashboardFilterLine';
import { DashboardHeader } from './DashboardHeader';
import { useDashboardView } from './DashboardViewContext';
import { usePendingLayoutChanges } from './hooks/usePendingLayoutChanges';
import { usePublishBufferedLayout } from './hooks/useBufferedLayout';
import { usePublishDashboardViewSnapshot } from './hooks/useDashboardViewSnapshot';
import { toPickerRange } from './date-range';
import { UNSAVED_ID } from './hooks/useDashboard';
import { CardSection } from './CardSection';
import { CardLibrary } from './CardLibrary';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';

const CARD_ENTITY = 'card';
const SECTION_ENTITY = 'section';
const DASHBOARD_ENTITY = 'dashboard';
const NO_CARD_IDS: string[] = [];

// FocusedChartSummary.type (from useSavedChartSummaries) is deliberately widened to a bare `string`
// for the agent-grounding module, which has no reason to import the dashboarding chart-type union.
// The resize funnel's `typeOf` needs the real `ChartType`, so this narrows it -- and doubles as the
// "type unknown" case resizeCardInList relies on: a value that isn't a real chart type name (stale
// metadata, a future type this build doesn't know) comes back `undefined`, same as a missing entry.
const isChartType = (value: string): value is ChartType => CHART_TYPES.some(t => t === value);

interface DashboardBuilderProps {
  config: DashboardConfig;
  // Persist the dashboard: one write per explicit Save or view-mode removal; edit-mode edits buffer
  // until Save. `onError` runs if the write fails or is refused.
  onPersist: (config: DashboardConfig, callbacks?: { onError?: () => void }) => void;
  // Whether this user may change the dashboard. Worked out once in DashboardView, which already
  // holds the config, and threaded from here exactly as `isEditMode` already is.
  canEdit: boolean;
  // Persist a new pair of role levels, and nothing else. A SEPARATE path from `onPersist` on
  // purpose: that one sends this builder's buffered layout, and an audience change must not commit
  // a half-finished layout as a side effect. Implemented in DashboardView beside `handlePersist`.
  onPermissionChange: (levels: ResolvedRoleLevels) => Promise<void>;
  // Leaves this dashboard for the dashboards list.
  onBack: () => void;
}

interface HistoryState {
  cards: DashboardCardData[];
  sections: DashboardSection[];
}
interface HistoryStore {
  entries: HistoryState[];
  index: number;
}

// Push a layout onto the local history, dropping any redo branch and keeping the last 50 entries.
const pushHistory = (prev: HistoryStore, entry: HistoryState): HistoryStore => {
  const limited = [...prev.entries.slice(0, prev.index + 1), entry].slice(-50);
  return { entries: limited, index: limited.length - 1 };
};

export const DashboardBuilder = ({
  config,
  onPersist,
  onBack,
  canEdit,
  onPermissionChange
}: DashboardBuilderProps) => {
  const {
    globalDateConfig,
    globalRange,
    applyDateConfig,
    dashboardFilters,
    dashboardFiltersWithoutPreview,
    setDashboardFilters,
    setPreviewDashboardFilters,
    dashboardFiltersRejected
  } = useDashboardView();
  const [isEditMode, setIsEditMode] = useState(false);
  // The date picker is live in VIEW mode too, so globalDateConfig can already hold a window the user
  // was only glancing at. Snapshot it as edit mode opens: Save then persists a date change only if
  // one was actually made while editing, and Discard restores what they were looking at.
  const [dateConfigOnEnterEdit, setDateConfigOnEnterEdit] = useState(globalDateConfig);
  const [showCardLibrary, setShowCardLibrary] = useState(false);
  // The agent's pending dashboard filters, previewed in the view and outside undo history, and the
  // Save they wait on (see pendingFilterFlow).
  const [filterFlow, dispatchFilterFlow] = useReducer(
    pendingFilterFlow,
    INITIAL_PENDING_FILTER_FLOW
  );
  const pendingDefaultFilters = filterFlow.pending;
  useEffect(() => {
    setPreviewDashboardFilters(filterFlow.pending);
  }, [filterFlow.pending, setPreviewDashboardFilters]);
  useEffect(() => {
    if (filterFlow.viewUpdate) {
      setDashboardFilters(filterFlow.viewUpdate.filters);
    }
  }, [filterFlow.viewUpdate, setDashboardFilters]);
  useEffect(() => {
    dispatchFilterFlow({ kind: 'echo', config });
  }, [config]);
  const [store, setStore] = useState<HistoryStore>({
    entries: [{ cards: config.cards, sections: config.sections }],
    index: 0
  });
  // The buffered dashboard name. Editable only in edit mode; committed with the layout on Save. Re-seeds
  // when the dashboard IDENTITY changes (switch / initial load) — keyed on config.id, not config, so a
  // background refetch of the same dashboard can't clobber an in-progress rename.
  const [nameDraft, setNameDraft] = useState(config.name);
  useEffect(() => {
    setNameDraft(config.name);
    // Only on dashboard-identity change; config.name is intentionally not a dep (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.id]);

  const cards = store.entries[store.index]?.cards ?? config.cards;
  const sections = store.entries[store.index]?.sections ?? config.sections;

  // Show the global comparison date range only when a chart on this dashboard uses one.
  const { cid: company } = useCompanyInfo();
  const queryClient = useQueryClient();
  const setError = useSetError();
  const cardChartIds = (cardList: DashboardCardData[]): ChartId[] =>
    cardList
      .filter(c => c.type === CardType.CHART && c.config)
      .map(c => (c.config as ChartCardConfig).chartId);
  const chartIds = useMemo(() => cardChartIds(cards), [cards]);
  const showComparison = useAnyChartUsesComparison(company, chartIds);

  // The chart-type lookup resizeCardInList needs to enforce the size matrix (see its doc comment).
  // Sourced from the dashboarding bootstrap the page has already loaded for the agent grounding
  // above, so this costs no extra request. An id missing from the map, or a type this build doesn't
  // recognize, resolves to `undefined` -- resizeCardInList's documented "leave it unchanged" case.
  const savedChartSummaries = useSavedChartSummaries(company);
  const chartTypeOf = useCallback(
    (chartId: ChartId): ChartType | undefined => {
      const { type } = savedChartSummaries.get(chartId) ?? {};
      return type && isChartType(type) ? type : undefined;
    },
    [savedChartSummaries]
  );

  // Re-seed local history when the dashboard's persisted layout changes from OUTSIDE the builder
  // (e.g. the agent appended a chart and the dashboard query refetched), so the new card renders
  // without a manual refresh. We compare placement signatures (not card identity) against what's
  // currently shown: the echo of our own save matches, so it's a no-op that leaves undo history
  // intact; an external change differs, so we reset history to the new layout. Reading `prev` inside
  // the updater avoids depending on every local edit (which would race the not-yet-arrived echo).
  //
  // A re-seed MERGES rather than replaces: the buffer can hold pending changes (a resize, or an
  // agent's remove or move), so taking the server's layout wholesale would silently discard them the
  // moment the agent appends a chart. The merge is three-way against the config it replaces, so the
  // new card arrives AND the pending changes survive. A switch to another dashboard (this builder is
  // not keyed per dashboard) has nothing to merge with and takes the new layout as-is.
  const previousConfig = useRef(config);
  useEffect(() => {
    const previous = previousConfig.current;
    previousConfig.current = config;
    setStore(prev => {
      const shown = prev.entries[prev.index]?.cards ?? config.cards;
      if (placementSignature(config.cards) === placementSignature(shown)) return prev;
      const merged =
        previous.id === config.id
          ? mergeExternalLayout(previous.cards, config.cards, shown)
          : config.cards;
      return { entries: [{ cards: merged, sections: config.sections }], index: 0 };
    });
  }, [config]);

  // Layout/section edits accrue into local history ONLY — nothing persists until Save changes. (This
  // is the commit boundary: it used to call onConfigChange here, auto-saving every edit.)
  const saveToHistory = useCallback(
    (nextCards: DashboardCardData[], nextSections: DashboardSection[] = sections) => {
      setStore(prev => pushHistory(prev, { cards: nextCards, sections: nextSections }));
    },
    [sections]
  );

  // Whether the buffer or the dashboard filters differ from what's persisted. Only meaningful in
  // VIEW mode, where there is otherwise no Save button to reveal — edit mode always shows it.
  const hasPendingChanges =
    pendingDefaultFilters !== undefined ||
    placementSignature(cards) !== placementSignature(config.cards);

  const canUndo = store.index > 0;
  const canRedo = store.index < store.entries.length - 1;

  // Undo/redo move the local history index only — like every other edit, they don't persist until Save.
  const handleUndo = () => {
    if (!canUndo) return;
    setStore(prev => ({ ...prev, index: prev.index - 1 }));
  };

  const handleRedo = () => {
    if (!canRedo) return;
    setStore(prev => ({ ...prev, index: prev.index + 1 }));
  };

  // Enter edit mode: nameDraft already tracks the persisted name (config is the baseline), so there's
  // nothing to snapshot for it — Discard reverts to `config`, the last-persisted state. The date
  // window is different: it lives in shared view state that view mode can already have changed, so
  // it gets an explicit baseline.
  const handleEnterEdit = () => {
    setDateConfigOnEnterEdit(globalDateConfig);
    setIsEditMode(true);
  };

  // Save changes: commit the buffered layout + name + date window as ONE write, then leave edit
  // mode. The date window is the picker's live SELECTION, so a dashboard saved on a preset reopens
  // on that preset rather than on the dates it happened to mean today.
  //
  // Only if it changed while editing, though. The picker is live in view mode, so browsing to a
  // different window and then entering edit mode to rename or move a card would otherwise make Save
  // quietly overwrite the dashboard's real default with whatever was last glanced at. Unchanged
  // since edit mode opened => persist what's already stored, untouched.
  const handleSaveChanges = () => {
    // Gated on isEditMode as well: a Save can now be triggered from VIEW mode (a pending resize),
    // and a window the user merely browsed to there is not a change to the dashboard's default.
    const dateConfigChangedWhileEditing = isEditMode && globalDateConfig !== dateConfigOnEnterEdit;
    // Before the write: a refusal reports its failure synchronously.
    dispatchFilterFlow({ kind: 'save', config });
    onPersist(
      withPendingDefaultFilters(
        {
          ...config,
          name: nameDraft.trim() || config.name,
          cards,
          sections,
          defaultDateConfig: dateConfigChangedWhileEditing
            ? globalDateConfig
            : config.defaultDateConfig
        },
        pendingDefaultFilters
      ),
      { onError: () => dispatchFilterFlow({ kind: 'saveFailed' }) }
    );
    Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAMES.DASHBOARD, entity: DASHBOARD_ENTITY });
    setIsEditMode(false);
  };

  // Discard changes: drop local history, the rename, any date change made while editing, and every
  // one of its charts' pending edits (e.g. a title just typed into a card, or an agent-proposed
  // change), so "Discard" from the dashboard level really means nothing here changed. The date
  // reverts to the snapshot rather than to the saved config — Discard undoes what was done in edit
  // mode, and a window the user was browsing before they opened it isn't part of that. A per-chart
  // working copy is otherwise invisible from here: it lives in the working-copy cache, not in this
  // dashboard's own config, so leaving it would silently resurrect it (still pending, exits still
  // showing) the moment edit mode is re-entered. Clears the UNION of the current local `cards` and the
  // persisted `config.cards`, not just `cards`: a chart removed locally during this edit session is
  // about to reappear once the revert below lands, and it needs its pending edit cleared too —
  // `chartIds` alone (scoped to the local list) would miss it entirely.
  const handleDiscardChanges = () => {
    setStore({ entries: [{ cards: config.cards, sections: config.sections }], index: 0 });
    dispatchFilterFlow({ kind: 'discard' });

    // In VIEW mode Discard only undoes the agent's pending changes: the browsed date window and a
    // card's own pending chart edit are not what it points at.
    if (!isEditMode) return;

    const idsToDrop = new Set([...chartIds, ...cardChartIds(config.cards)]);
    setNameDraft(config.name);
    applyDateConfig(dateConfigOnEnterEdit);
    idsToDrop.forEach(chartId => clearWorkingCopyFor(queryClient, company, chartId));
    setIsEditMode(false);
  };

  const handleMoveCard = useCallback(
    (cardId: string, targetSectionId: string | null, index: number) => {
      const card = cards.find(c => c.id === cardId);
      if (!card) return;
      const moved: DashboardCardData = { ...card, sectionId: targetSectionId ?? undefined };
      const peers = cards.filter(c => c.id !== cardId && (c.sectionId ?? null) === targetSectionId);
      const others = cards.filter(
        c => c.id !== cardId && (c.sectionId ?? null) !== targetSectionId
      );
      // The drop is an index in the target grid's array order (see resolveInsertion); autoCompact
      // turns that order into positions.
      saveToHistory([...others, ...autoCompact(insertAtIndex(moved, peers, index))]);
    },
    [cards, saveToHistory]
  );

  // Removing a card is the one edit offered from BOTH modes (the card's ⋮ menu in view mode, the ✕ in
  // edit mode), so it's one handler with each mode's own commit contract:
  //  - edit mode: join the buffered history like every other layout edit; Save changes commits it.
  //  - view mode: persist immediately — matching how a chart ARRIVES (the agent appends and saves in
  //    one step). Only the removal is saved, against the SAVED layout: the buffer can hold the
  //    agent's pending changes, which must stay pending behind Save/Discard. The card also leaves
  //    every buffered layout, so they stay pending without it and undo can't bring it back.
  // Either way the survivors compact, so no hole is left behind.
  // Nothing else needs cleanup: the chart's working copy and its date/KPI view overrides are keyed by
  // chart, not by placement, and the chart outlives the placement (it's still in the library).
  const handleRemoveCard = (id: string) => {
    if (isEditMode) {
      saveToHistory(removeCard(cards, id));
      Mixpanel.track(MxE.RecordDeleted, { page: PAGE_NAMES.DASHBOARD, entity: CARD_ENTITY });
      return;
    }
    const savedCards = removeShownCardFromSaved(config.cards, cards, id);
    if (savedCards === config.cards) {
      setError({
        message: "Couldn't remove this card. Save or discard the pending changes first."
      });
      return;
    }
    onPersist({ ...config, cards: savedCards });
    setStore(prev => ({
      ...prev,
      entries: prev.entries.map(entry => ({ ...entry, cards: removeCard(entry.cards, id) }))
    }));
    Mixpanel.track(MxE.RecordDeleted, { page: PAGE_NAMES.DASHBOARD, entity: CARD_ENTITY });
  };

  // resizeCardInList (dashboard-utils.ts) is used by two paths: the card's own menu, here, and the
  // agent's set_card_size below (via applyLayoutChanges). They must not drift — a chat resize has to
  // land in the buffer with the same clamping, or the two produce different layouts from the same
  // request. It's a pure function, extracted there rather than kept as a local closure, so it has a
  // direct unit-test seam and so the ordering logic it must respect (see its doc comment) lives
  // beside autoCompact's.

  // Buffered in BOTH modes. Resizing from view mode used to write straight to the dashboard, which
  // is a surprise in a mode called "view" and has no confirm in front of it the way removal does. It
  // becomes a PENDING change instead: the header offers Save/Discard once the buffer differs from
  // what's stored, and the change carries into edit mode untouched (the buffer is shared and
  // entering edit mode does not re-seed it).
  const handleChangeCardSize = (id: string, size: CardSize) => {
    const next = resizeCardInList(cards, id, size, chartTypeOf);
    if (next !== cards) {
      saveToHistory(next);
    }
  };

  // Appends the new card, then compacts immediately -- exactly what the read path
  // (`dashboardToConfig`) does on every load. `findBestPosition` used to place it, but that result
  // is discarded the instant compaction runs (see compactBySection above), which let the card render
  // in a hole mid-dashboard and then jump to the bottom the moment it was saved and reloaded.
  // Compacting here means what's rendered IS what a save/reload produces, and the card lands where
  // a newly added chart belongs: at the end, since it's appended last in array order. The position
  // below is a placeholder only -- compactBySection replaces it unconditionally, and unlike
  // findBestPosition (which can return null if the scan runs out of rows) compaction always finds a
  // slot, so there's no longer a case where adding a card silently does nothing.
  const handleAddCard = (type: CardTypeValue, size: CardSize) => {
    const newCard: DashboardCardData = {
      id: `card-${Date.now()}`,
      type,
      size,
      position: { x: 0, y: 0 }
    };
    saveToHistory(compactBySection([...cards, newCard]));
    Mixpanel.track(MxE.RecordCreated, { page: PAGE_NAMES.DASHBOARD, entity: CARD_ENTITY });
  };

  // The agent's changes wait for Save/Discard, never a write: card changes go through the card
  // controls' helpers into the buffer, dashboard filters into a preview.
  //
  // Cleared BEFORE applying, so a re-render can't replay it: the note is one-shot, and the resulting
  // buffer change is itself what re-renders us. Applied inside the store updater, against whatever
  // layout is current by then, so it composes with a re-seed landing in the same render (the agent
  // appended a chart in the same turn) instead of overwriting it with a stale `cards`.
  const { pendingLayoutChanges, clearPendingLayoutChanges } = usePendingLayoutChanges(
    company,
    config.id === UNSAVED_ID ? null : config.id
  );
  useEffect(() => {
    if (pendingLayoutChanges.length === 0) return;
    clearPendingLayoutChanges();
    const nextFilters = pendingDashboardFilters(pendingLayoutChanges);
    if (nextFilters) {
      dispatchFilterFlow({
        kind: 'agentSetFilters',
        filters: nextFilters,
        config,
        viewFilters: dashboardFiltersWithoutPreview
      });
    }
    setStore(prev => {
      const current = prev.entries[prev.index] ?? {
        cards: config.cards,
        sections: config.sections
      };
      // chartTypeOf is threaded through so an agent-driven resize is coerced by the chart-type size
      // matrix exactly like a menu resize (see resizeCardInList's doc comment) -- otherwise the
      // agent could echo a size the matrix forbids for that chart type.
      const next = applyLayoutChanges(current.cards, pendingLayoutChanges, chartTypeOf);
      return next === current.cards ? prev : pushHistory(prev, { ...current, cards: next });
    });
    // The note is already cleared above; `config` and the view's filters are read as of this note.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingLayoutChanges]);

  // What the agent is grounded on: this buffer, pending changes included, not the saved layout.
  usePublishBufferedLayout(company, config.id === UNSAVED_ID ? null : config.id, cards);

  const viewSnapshot = useMemo(
    () => ({ dashboardFilters, dateRange: toPickerRange(globalRange) }),
    [dashboardFilters, globalRange]
  );
  usePublishDashboardViewSnapshot(
    company,
    config.id === UNSAVED_ID ? null : config.id,
    viewSnapshot
  );

  const handleAddSection = () => {
    const newSection: DashboardSection = {
      id: `section-${Date.now()}`,
      title: 'New Section',
      order: sections.length
    };
    saveToHistory(cards, [...sections, newSection]);
    Mixpanel.track(MxE.RecordCreated, { page: PAGE_NAMES.DASHBOARD, entity: SECTION_ENTITY });
  };

  const handleRemoveSection = (sectionId: string) => {
    const nextCards = autoCompact(cards.filter(c => c.sectionId !== sectionId));
    const nextSections = sections.filter(s => s.id !== sectionId);
    saveToHistory(nextCards, nextSections);
    Mixpanel.track(MxE.RecordDeleted, { page: PAGE_NAMES.DASHBOARD, entity: SECTION_ENTITY });
  };

  const handleUpdateSectionTitle = (sectionId: string, title: string) => {
    saveToHistory(
      cards,
      sections.map(s => (s.id === sectionId ? { ...s, title } : s))
    );
  };

  const handleLooseCardDrop = useCallback(
    (cardId: string, index: number) => handleMoveCard(cardId, null, index),
    [handleMoveCard]
  );

  // Sorted into reading order. The grid places cards by absolute position, so this is invisible at
  // full width — but below the breakpoint cards auto-flow and DOM order becomes the order they
  // appear in, which would otherwise be whatever order they happen to sit in the array.
  const looseCards = cards.filter(c => !c.sectionId).sort(byGridOrder);
  // Each grid's card ids in array order, not the reading order above — the order a drop index
  // counts in. Memoized so the grids don't re-register their drop zones on every render.
  const cardIdsByZone = useMemo(() => {
    const byZone = new Map<string | null, string[]>();
    cards.forEach(c => {
      const zone = c.sectionId ?? null;
      byZone.set(zone, [...(byZone.get(zone) ?? []), c.id]);
    });
    return byZone;
  }, [cards]);

  return (
    <DragProvider>
      <div className='dashboard-builder'>
        <DragOverlay />
        <DashboardHeader
          isEditMode={isEditMode}
          hasPendingChanges={hasPendingChanges}
          canEdit={canEdit}
          viewRoleRequired={config.viewRoleRequired}
          editRoleRequired={config.editRoleRequired}
          onPermissionChange={onPermissionChange}
          dashboardId={config.id}
          nameDraft={nameDraft}
          onNameChange={setNameDraft}
          canUndo={canUndo}
          canRedo={canRedo}
          showComparison={showComparison}
          onEnterEdit={handleEnterEdit}
          onSaveChanges={handleSaveChanges}
          onDiscardChanges={handleDiscardChanges}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onAddSection={handleAddSection}
          onAddCards={() => {
            setShowCardLibrary(true);
            Mixpanel.track(MxE.ModalOpened, { page: PAGE_NAMES.DASHBOARD, modal: 'card_library' });
          }}
          onBack={onBack}
        />
        <DashboardFilterLine
          filters={dashboardFilters}
          filtersRejected={dashboardFiltersRejected}
        />
        <div className='dashboard-builder-canvas'>
          {[...sections]
            .sort((a, b) => a.order - b.order)
            .map(section => (
              <CardSection
                key={section.id}
                section={section}
                isEditMode={isEditMode}
                onRemove={handleRemoveSection}
                onUpdateTitle={handleUpdateSectionTitle}
                onDrop={(cardId, index) => handleMoveCard(cardId, section.id, index)} // TODO: stabilize with useCallback per section
                cardIds={cardIdsByZone.get(section.id) ?? NO_CARD_IDS}
              >
                {cards
                  .filter(c => c.sectionId === section.id)
                  .sort(byGridOrder)
                  .map(card => (
                    <DashboardCard
                      key={card.id}
                      card={card}
                      cardIndex={cards.indexOf(card)}
                      dashboardId={config.id}
                      isEditMode={isEditMode}
                      canEdit={canEdit}
                      onRemove={handleRemoveCard}
                      onChangeSize={handleChangeCardSize}
                      savedIgnoreDashboardFilters={
                        savedCardFor(config.cards, cards, card.id)?.config?.ignoreDashboardFilters
                      }
                    />
                  ))}
              </CardSection>
            ))}
          <DashboardGrid
            zoneId={null}
            onDrop={handleLooseCardDrop}
            cardIds={cardIdsByZone.get(null) ?? NO_CARD_IDS}
          >
            {/* Removing the last chart is now reachable, so the grid needs something to say when it's
                bare — otherwise the dashboard reads as broken rather than empty. Rendered INSIDE the
                grid so it stays a drop target for a card dragged out of a section. */}
            {looseCards.length === 0 && sections.length === 0 && (
              <div className='dashboard-grid-empty'>
                No charts on this dashboard yet — ask the agent to add one.
              </div>
            )}
            {looseCards.map(card => (
              <DashboardCard
                key={card.id}
                card={card}
                cardIndex={cards.indexOf(card)}
                dashboardId={config.id}
                isEditMode={isEditMode}
                canEdit={canEdit}
                onRemove={handleRemoveCard}
                onChangeSize={handleChangeCardSize}
                savedIgnoreDashboardFilters={
                  savedCardFor(config.cards, cards, card.id)?.config?.ignoreDashboardFilters
                }
              />
            ))}
          </DashboardGrid>
        </div>
        <CardLibrary
          isOpen={showCardLibrary}
          onClose={() => setShowCardLibrary(false)}
          onAddCard={handleAddCard}
        />
      </div>
    </DragProvider>
  );
};
